import React, { useEffect } from 'react';
import { Box, Container, Grid, Card, CardContent, Typography } from '@mui/material';
import ApartmentIcon from '@mui/icons-material/Apartment';
import SecurityIcon from '@mui/icons-material/Security';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import TimelineIcon from '@mui/icons-material/Timeline';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import BarChartIcon from '@mui/icons-material/BarChart';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LayersIcon from '@mui/icons-material/Layers';
import { getTranslation } from 'common';
import { useTranslations } from 'hooks';
import { AutoAwesome, Dataset } from '@mui/icons-material';
import AssistantIcon from '@mui/icons-material/Assistant';
import DatasetIcon from '@mui/icons-material/Dataset';
import FlareIcon from '@mui/icons-material/Flare';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';

export default function LandingPageCards({ lang }: { lang?: string }) {
    const translations = useTranslations()
    useEffect(() => {
        if (lang) {
            // console.log("LandingPageCards lang:", lang);
        }
    }, [lang]);

    const features = [
        {
            icon: ApartmentIcon,
            title: getTranslation(translations, 'card_title.workspace_organizations'),
            description: getTranslation(translations, 'card_description.workspace_organizations'),
            bgColor: '#E3F2FD'
        },
        {
            icon: AutoAwesome,
            title: getTranslation(translations, "card.title.ai_assistant"),
            description: getTranslation(translations, "card.description.ai_assistant"),
            bgColor: '#E3F2FD',
        },
        {
            icon: DocumentScannerIcon,
            title: getTranslation(translations, "card.title.automatic_docs"),
            description: getTranslation(translations, "card.description.automatic_docs"),
            bgColor: '#E3F2FD'
            //'#E8F5E9'
        },
        {
            icon: DescriptionIcon,
            title: getTranslation(translations, 'card_title.reusable_templates'),
            description: getTranslation(translations, 'card_description.reusable_templates'),
            bgColor: '#E3F2FD'
        },
        {
            icon: AssistantIcon,
            title: getTranslation(translations, "card.title.smart_suggestions"),
            description: getTranslation(translations, "card.description.smart_suggestions"),
            bgColor: '#E3F2FD',
        },
        {
            icon: EditIcon,
            title: getTranslation(translations, "card_title.intuitive_bpmn_editor"),
            description: getTranslation(translations, "card_description.intuitive_bpmn_editor"),
            bgColor: '#E3F2FD'
        },
        {
            icon: CheckBoxIcon,
            title: getTranslation(translations, "card_title.task_management"),
            description: getTranslation(translations, "card_description.task_management"),
            bgColor: '#E3F2FD'
        },
        {
            icon: ChatBubbleIcon,
            title: getTranslation(translations, "card_title.team_collaboration"),
            description: getTranslation(translations, "card_description.team_collaboration"),
            bgColor: '#E3F2FD'
        },
        {
            icon: TimelineIcon,
            title: getTranslation(translations, "card_title.live_monitor"),
            description: getTranslation(translations, "card_description.live_monitor"),
            bgColor: '#E3F2FD'
        },
        {
            icon: SmartToyIcon,
            title: getTranslation(translations, "card_title.robot_and_automation"),
            description: getTranslation(translations, "card_description.robot_and_automation"),
            bgColor: '#E3F2FD'
        },
        {
            icon: DatasetIcon,
            title: getTranslation(translations, "card.title.automatic_data_extraction"),
            description: getTranslation(translations, "card.description.automatic_data_extraction"),
            bgColor: '#E3F2FD',
        },
        {
            icon: BarChartIcon,
            title: getTranslation(translations, "card_title.advanced_statistics"),
            description: getTranslation(translations, "card_description.advanced_statistics"),
            bgColor: '#E3F2FD'
        },
        {
            icon: AccountTreeIcon,
            title: getTranslation(translations, "card_title.conditional_logic"),
            description: getTranslation(translations, "card_description.condtional_logic"),
            bgColor: '#E3F2FD'
        },
        {
            icon: AccessTimeIcon,
            title: getTranslation(translations, "card_title.events_and_timers"),
            description: getTranslation(translations, "card_description.events_and_timers"),
            bgColor: '#E3F2FD'
        },
        {
            icon: LayersIcon,
            title: getTranslation(translations, "generic.subprocess"),
            description: getTranslation(translations, "card_description.subprocess"),
            bgColor: '#E3F2FD'
        },
        {
            icon: FlareIcon,
            title: getTranslation(translations, "card.title.optimization"),
            description: getTranslation(translations, "card.description.optimization"),
            bgColor: '#E3F2FD',
        },
    ];

    return (
        <Box sx={{ py: 8, bgcolor: "background.default" }} id="features">
            <Container maxWidth="lg">
                <Grid container spacing={6}>
                    {features.map((feature, index) => {
                        const IconComponent = feature.icon;
                        return (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                        bgcolor: 'background.paper',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                            transform: 'translateY(-4px)'
                                        }
                                    }}
                                >
                                    <CardContent sx={{ p: 3 }}>
                                        <Box
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: 2,
                                                bgcolor: (theme) =>
                                                    theme.palette.mode === "dark" ? "rgba(84, 189, 193, 0.16)" : feature.bgColor,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mb: 2
                                            }}
                                        >
                                            <IconComponent sx={{ color: 'primary.main', fontSize: 28 }} />
                                        </Box>

                                        <Typography
                                            variant="h6"
                                            component="h3"
                                            sx={{
                                                fontWeight: 600,
                                                color: 'text.primary',
                                                mb: 1.5,
                                                fontSize: '1.1rem'
                                            }}
                                        >
                                            {feature.title}
                                        </Typography>

                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'text.secondary',
                                                lineHeight: 1.6,
                                                fontSize: '0.875rem'
                                            }}
                                        >
                                            {feature.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            </Container>
        </Box>
    );
}
